import React, {useState} from 'react';
import { Input, Space, Row, Col, Divider, Drawer, Spin, InputNumber, Popover } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import { AddRounded, WarningAmberRounded } from "@mui/icons-material";
import { Percent, TrendingUp, LineChart, Activity, AudioLinesIcon } from "lucide-react";
import {updatePrecision} from "../../../store/slices/builder.slice";
import RichSlider from "../../../components/Inputs/RichSlider";
import {Disc} from 'lucide-react'
import PercentageBar from "../../../modules/Charts/PercentageBar";
import { useGptResponses } from "../../../modules/Inference/useGptResponses";
import GradientIcon from "../../../components/Icons/GradientIcon";
import {selectResponseById} from "../../../store/slices/gpt.slice";
import ViewableResponse from "../../../modules/Inference/components/ViewableResponse";
import {isUUID} from "../../../utils/strings";

const Stat = ({ metricName, title, value, postfix, Icon = Activity }) => {
  const { submitMetricToTarget, responseId, loadingResponse } = useGptResponses();
  const [showDrawer, setShowDrawer] = useState(false);
  const [targetValue, setTargetValue] = useState(100);
  const [popoverVisible, setPopoverVisible] = useState(false); // State for popover visibility
  const response = useSelector(state => state.gptApi.responses?.[responseId] ?? {});
  const portfolioItems = useSelector(state => state.builder.portfolioBuilder.items);

  const handleMetricToTarget = () => {
    const tickerWeights = Array.isArray(portfolioItems) ? portfolioItems.filter(x => !isUUID(x.id)).map(item => ({
      symbol: item.id, weight: item.weight
    })) : [];
    submitMetricToTarget(metricName, value, targetValue, tickerWeights);
    setShowDrawer(true);
    setPopoverVisible(false); // Hide popover after submitting
  };

  const handleTargetValueChange = (newValue) => {
    setTargetValue(newValue);
  };

  const handleVisibleChange = (visible) => {
    setPopoverVisible(visible);
  };

  const popoverContent = (
    <div>
      <InputNumber
        min={1}
        max={300}
        value={targetValue}
        onChange={handleTargetValueChange}
        style={{ width: 80 }}
      />
      <Button onClick={handleMetricToTarget} style={{ marginLeft: 10 }}>
        Submit
      </Button>
    </div>
  );

  return (
    <Row justify={'space-between'} className={'py-3'}>
      <Col>
        <Popover
          content={popoverContent}
          trigger="hover"
          open={popoverVisible}
          onOpenChange={handleVisibleChange}
          placement="bottom"
        >
          <Row justify={'start'} align={'middle'} wrap={false} gutter={6}>
            <Col>
              <div className="w-6 h-6 flex items-center justify-center">
                <GradientIcon
                  icon={Icon}
                  size={20}
                  gradientStops={[
                    { offset: '0%', color: '#FF0080' },
                    { offset: '50%', color: '#7928CA' },
                    { offset: '100%', color: '#FF0080' }
                  ]}
                />
              </div>
            </Col>
            <Col>
              <span className="text-sm text-left text-gray-900">{title}</span>
            </Col>
          </Row>
        </Popover>
      </Col>
      <Col>
        <span className={`text-md font-semibold`}>
          {value}{postfix}
        </span>
      </Col>
      <Drawer open={showDrawer} onClose={() => setShowDrawer(false)} width={700}>
        {loadingResponse && <div className={'mx-auto p-6'}>
          <Spin />
        </div>}
        {response.hasOwnProperty('data') && (
          <div className={'p-6'}>
            <ViewableResponse markdownText={response?.data} title={`${metricName} Analysis`} />
          </div>
        )}
      </Drawer>
    </Row>
  )
}

const Stats = ({portfolioStats = {}}) => {

  const stats = [
    { metricName: 'volatility', title: "3 Month Vol", value: portfolioStats?.threeMonthVol ?? 0, postfix: '%' },
    { metricName: 'beta_spy_1_year', title: "Beta (1Y SPY)", value: portfolioStats?.oneYearBetaSpy ?? 0, Icon: TrendingUp, postfix: '%' },
    { metricName: 'corr_spy_1_year', title: "Corr (1Y SPY)", value: portfolioStats?.oneYearCorrSpy ?? 0, Icon: LineChart, postfix: '%' }
  ];
  return (
    <div className={`grid xs:grid-cols-1 lg:grid-cols-2 gap-2`}>
      {stats.map(({ metricName, title, value, postfix }) => (
        <div key={title} className={'border-b border-gray-200'}>
            <Stat metricName={metricName}  title={title} value={value} postfix={postfix} />
        </div>
      ))}
    </div>
  );
};


const PercentComponent = ({totalValue})=> (
    <div>
      <div className={`flex justify-between items-center gap-4 w-full max-w-2xl`}>
        <div className="text-4xl font-semibold">
          {totalValue}
        </div>
        <div className={'flex flex-row'}>
          <div className={'mx-4'} style={{height: 100, width: 40}}>
            <PercentageBar value={totalValue * 0.01} fillColor={'#2dd4bf'}/>
            {/*<Progress percent={totalValue} size={[160]} type={'circle'} />*/}
          </div>
          <div style={{height: 100, width: 40}}>
            <PercentageBar value={(100 - totalValue) * 0.01} fillColor={'#ef4444'}/>
            {/*<Progress percent={totalValue} size={[160]} type={'circle'} />*/}
          </div>
        </div>


      </div>
    </div>
)

const PortfolioDetails = ({title, onTitleChange, onSave, isNew, extraLeft, portfolioStats}) => {
  const dispatch = useDispatch();
  const precision = useSelector(state => state.builder.precision);

  const totalValue = portfolioStats?.totalWeight?.toFixed(precision);

  const handleUpdatePrecision = (newValue) => {
    dispatch(updatePrecision(newValue));
  }

  return (
      <div className={'w-full'}>
        <Row align={'middle'} justify={'space-between'} wrap={true} gutter={[16, 16]}>
          <Col>
            <Row align={'middle'} gutter={[8, 8]}>
              <Col>
                <Input
                    placeholder="Portfolio Title"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    status={title === '' ? 'error' : 'success'}
                />
              </Col>
              <Col>
                <Space>

                <Button startIcon={<AddRounded/>}
                        color="primary"
                        variant="outlined"
                        className={'pill'}
                        onClick={() => onSave({saveAsNew: true})}
                >
                  Save as New
                </Button>
              {!isNew && (
                  <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onSave({saveAsNew: false})}
                      startIcon={<WarningAmberRounded/>}
                  >
                    Update
                  </Button>
              )}
                </Space>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row gutter={16}>
              <Col>
                <PercentComponent totalValue={totalValue}/>

              </Col>
              <Col>
                <Stats portfolioStats={portfolioStats}/>

              </Col>
            </Row>
          </Col>

        </Row>
      </div>
  )
}

export default PortfolioDetails;